import { Box }               from '@chakra-ui/react';
import { Button }            from '@/components/ui/button';
import { CroppedAreaPixels } from '@/tools/images';
import Cropper               from 'react-easy-crop'
import { DialogBody }        from '@/components/ui/dialog';
import { DialogContent }     from '@/components/ui/dialog';
import { DialogFooter }      from '@chakra-ui/react';
import { DialogRoot }        from '@/components/ui/dialog';
import { Dispatch }          from 'react';
import { Grid }              from '@chakra-ui/react';
import { GridItem }          from '@chakra-ui/react';
import React                 from 'react';
import { SetStateAction }    from 'react';
import { Slider }            from '@/components/ui/slider'
import { Text }              from '@chakra-ui/react';
import { getCroppedImage }   from '@/tools/images';
import { useTranslation }    from 'react-i18next';
import { toaster }           from '@/components/ui/toaster';
import imageCompression      from 'browser-image-compression';

interface ICropperModalProps {
	imageSource: string;
	imageType: string;
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	setIsLoading?: Dispatch<SetStateAction<boolean>>;
	onSubmit: (file: File) => Promise<void>;
	onSuccess?: () => void;
	onError?: () => void;
	aspect?: number;
}

const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8 Mo
const MIN_QUALITY = 0.4;

const CropperModal = (props: ICropperModalProps) => {
	const { imageSource, isOpen, setIsOpen, setIsLoading, onSubmit, onSuccess, onError, aspect = 3 / 4 } = props;
	const { t } = useTranslation();
	const [crop, setCrop] = React.useState({ x: 0, y: 0 });
	const [zoom, setZoom] = React.useState(1);
	const [rotation, setRotation] = React.useState(0);
	const [croppedAreaPixels, setCroppedAreaPixels] = React.useState<CroppedAreaPixels>({width: 0, height: 0, x: 0, y: 0});
	

	const onCropComplete = (_croppedArea: unknown, croppedAreaPixels: CroppedAreaPixels) => {
		setCroppedAreaPixels(croppedAreaPixels);
	}

	const submit = async () => {
		try {
			if (setIsLoading) {
				setIsLoading(true);
			}

			let quality = 0.9;
			let blob: Blob | null = null;
	
			while (quality >= MIN_QUALITY) {

				const croppedImage = await getCroppedImage(
					imageSource,
					croppedAreaPixels,
					rotation,
					quality
				);
	
				if (!croppedImage){ 
					toaster.create({
						title: t('app.errors.image.croppedImageGenerationFailed'),
						type: 'error'
					});
					throw new Error(t('app.errors.image.croppedImageGenerationFailed'));
				};

				blob = croppedImage;
	
				if (blob.size <= MAX_FILE_SIZE) {
					break; // ✅ taille acceptable
				}				
	
				quality -= 0.1;
			}
	
			if (!blob) {
				toaster.create({
					title: t('app.errors.image.compressionFailed'),
					type: 'error'
				});
				throw new Error(t('app.errors.image.compressionFailed'));
			}
	
			if (blob.size > MAX_FILE_SIZE) {
				toaster.create({
					title: t('app.errors.image.tooBig'),
					type: 'error'
				});
				throw new Error(t('app.errors.image.tooBig'));
			}

			const file = new File([blob], 'profilePicture', { type: 'image/jpeg' });

			const compressedBlob = await imageCompression(file, {
				maxSizeMB: 1,
				maxWidthOrHeight: 500,
			});

			const compressedFile = new File([compressedBlob], 'profilePicture', { type: 'image/jpeg' });

			await onSubmit(compressedFile);

			if (onSuccess) {
				onSuccess();
			}

		} catch (error) {
			if (onError) {
				onError();
			}
			console.log(error);
		} finally {
			if (setIsLoading) {
				setIsLoading(false);
			}
		}
	}
	
	return (
		<DialogRoot 
			open={isOpen}
			size="cover"
		>
			<DialogContent>
				<DialogBody 
					position="relative"
				>
					<Cropper
						image={imageSource}
						crop={crop}
						rotation={rotation}
						zoom={zoom}
						aspect={aspect}
						onCropChange={setCrop}
						onRotationChange={setRotation}
						onCropComplete={onCropComplete}
						onZoomChange={(v) => setZoom(v)}
					/>
				</DialogBody>
				<DialogFooter
					paddingLeft={{ base: "10px", lg: "70px"}}
					paddingRight={{ base: "10px", lg: "70px"}}
					paddingTop={{ base: "18px", lg: "unset"}}
				>
					<Grid
						width="100%"
						height={{ base: "40px", lg: "100px"}}
						gridTemplateColumns={{ base: "1fr", lg: "1fr 1fr 1fr" }}
						gap="100px"
					>
						<GridItem
							alignSelf="center"
							hideBelow="lg"
						>
							<Box
								display="flex"
								alignItems="center"
								width="100%"
								gap="20px"
							>
								<Text>{t('pages.personalInformation.personalInformationBox.modals.cropper.actions.zoom')}</Text>
								<Slider 
									width="100%"
									value={[zoom]}
									min={1}
									max={3}
									step={0.1}
									onValueChange={(value) => {									
										setZoom(value.value[0]);
									}}
								/>
							</Box>
						</GridItem>
						<GridItem
							alignSelf="center"
							hideBelow="lg"
						>
							<Box
								display="flex"
								alignItems="center"
								width="100%"
								gap="20px"
							>
								<Text>{t('pages.personalInformation.personalInformationBox.modals.cropper.actions.rotate')}</Text>
								<Slider 
									width="100%"
									value={[rotation]}
									min={0}
									max={360}
									step={1}
									onValueChange={(value) => {
										setRotation(value.value[0]);
									}}
								/>
							</Box>
						</GridItem>
						<GridItem
							alignSelf="center"
							justifySelf="center"
							width="100%"
						>
							<Grid
								gridTemplateColumns="1fr 1fr"
								gap={{ base: "10px", lg: "20px"}}
								width="100%"
								alignContent="center"
								justifyContent="center"
							>
								<GridItem 
									width="100%"
								>
									<Button 
										width="100%"
										variant="outline"
										onClick={() => setIsOpen(false)}
									>
										{t('pages.personalInformation.personalInformationBox.modals.cropper.actions.cancel')}
									</Button>
								</GridItem>

								<GridItem width="100%">
									<Button 
										width="100%"
										height="40px"
										onClick={() => {
											submit();
											setIsOpen(false);
										}}
									>
										{t('pages.personalInformation.personalInformationBox.modals.cropper.actions.save')}
									</Button>
								</GridItem>
								
							</Grid>
						</GridItem>
					</Grid>
					
				</DialogFooter>
			</DialogContent>
		</DialogRoot>
		
	)
}

export default CropperModal